import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, LogOut, Plus, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { chatApi, calendarApi, MANGO_PERSONAL_URL } from '../services/api';
import { GroupThread, GroupManage, CalendarWeekStrip, type ChatGroup as Group } from 'shared-ui';
import { TOKEN_KEY } from '../constants';

interface CalendarItem {
  id: string;
  type: string;
  title: string;
  startTime: string;
  endTime?: string;
  groupId?: string;
}

const DAY_COUNT = 14;
const today = new Date();

const groupLabel = (g: Group): string =>
  g.otherMember ? [g.otherMember.firstName, g.otherMember.lastName].filter(Boolean).join(' ') : g.name;

/**
 * Social Circle's paired chat + calendar screen. Mobile-first master/detail: with no group
 * selected, shows the circle list + upcoming events; selecting a group swaps in the shared
 * GroupThread pane for it (same component mango-chat's GroupDetail wraps - see
 * shared/ui/chat/GroupThread.tsx). A wider side-by-side layout for desktop is a natural
 * follow-on once this is proven, not built here.
 */
const Circle: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [events, setEvents] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [managingGroupId, setManagingGroupId] = useState<string | null>(null);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupFriends, setNewGroupFriends] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  const load = () => {
    setLoading(true);
    const startDate = today.toISOString().split('T')[0];
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + DAY_COUNT - 1).toISOString().split('T')[0];
    Promise.all([
      chatApi.groups.mine(),
      calendarApi.getEvents({ startDate, endDate }),
    ]).then(([groupsRes, eventsRes]) => {
      setGroups(Array.isArray(groupsRes?.data) ? groupsRes.data : []);
      setEvents(Array.isArray(eventsRes?.data) ? eventsRes.data : []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Personal circles only (per the plan's scope discipline) - community/blast groups are
  // mango-chat's broader remit, not what Social Circle is for.
  const circleGroups = useMemo(() => groups.filter((g) => g.type === 'personal' || g.type === 'dm'), [groups]);
  const upcomingEvents = useMemo(
    () => events.slice().sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()).slice(0, 5),
    [events]
  );
  const selectedGroup = circleGroups.find((g) => g.id === selectedGroupId) || null;
  const managingGroup = circleGroups.find((g) => g.id === managingGroupId) || null;

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || creatingGroup) return;
    setCreatingGroup(true);
    try {
      const mangoIds = newGroupFriends.split(',').map((v) => v.trim()).filter(Boolean);
      const res = await chatApi.groups.create(newGroupName.trim(), mangoIds);
      if (res?.success && res.data?.id) {
        setShowNewGroup(false);
        setNewGroupName('');
        setNewGroupFriends('');
        load();
        setSelectedGroupId(res.data.id);
      }
    } finally {
      setCreatingGroup(false);
    }
  };

  const formatTime = (s: string) => new Date(s).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  if (managingGroup) {
    return (
      <GroupManage
        groupId={managingGroup.id}
        title={groupLabel(managingGroup)}
        currentUser={user ? { id: user.id, firstName: user.firstName, lastName: user.lastName } : null}
        isAdmin={managingGroup.myRole === 'admin' || managingGroup.myRole === 'moderator'}
        api={chatApi}
        onBack={() => setManagingGroupId(null)}
      />
    );
  }

  if (selectedGroup) {
    return (
      <GroupThread
        groupId={selectedGroup.id}
        title={groupLabel(selectedGroup)}
        currentUser={user ? { id: user.id, firstName: user.firstName, lastName: user.lastName, preferredLanguage: user.preferredLanguage } : null}
        api={chatApi}
        mangoPersonalUrl={MANGO_PERSONAL_URL}
        authToken={localStorage.getItem(TOKEN_KEY)}
        onBack={() => setSelectedGroupId(null)}
        onManage={() => setManagingGroupId(selectedGroup.id)}
      />
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#0f0f12' }}>
      <header style={{ padding: '16px', paddingTop: 'calc(16px + env(safe-area-inset-top))', background: '#18181b', borderBottom: '1px solid #27272a' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>Social Circle</h1>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={() => setShowNewGroup((v) => !v)} style={{ padding: 8, background: showNewGroup ? '#27272a' : 'transparent', border: 'none', color: showNewGroup ? '#ff6b35' : '#71717a', cursor: 'pointer', borderRadius: 8 }}>
              <Plus size={20} />
            </button>
            <button type="button" onClick={() => { logout(); navigate('/login'); }} style={{ padding: 8, background: 'transparent', border: 'none', color: '#71717a', cursor: 'pointer', borderRadius: 8 }}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
        {showNewGroup && (
          <div style={{ marginBottom: 12, padding: 12, background: '#27272a', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Circle name (e.g. Dinner squad)" style={{ padding: '10px 12px', background: '#18181b', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14 }} />
            <input type="text" value={newGroupFriends} onChange={(e) => setNewGroupFriends(e.target.value)} placeholder="Friends' mango IDs, comma separated" style={{ padding: '10px 12px', background: '#18181b', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14 }} />
            <button type="button" onClick={handleCreateGroup} disabled={!newGroupName.trim() || creatingGroup} style={{ padding: '10px 12px', background: !newGroupName.trim() || creatingGroup ? '#3f3f46' : 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: !newGroupName.trim() || creatingGroup ? 'not-allowed' : 'pointer' }}>
              {creatingGroup ? 'Creating...' : 'Create circle'}
            </button>
          </div>
        )}
        <CalendarWeekStrip anchorDate={today} events={events} dayCount={DAY_COUNT} alignToWeekStart={false} selectedDayKey={undefined} onSelectDay={() => {}} />
      </header>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {upcomingEvents.length > 0 && (
          <div>
            <p style={{ margin: 0, padding: '12px 16px 4px', fontSize: 12, fontWeight: 600, color: '#71717a', textTransform: 'uppercase' }}>Coming up</p>
            {upcomingEvents.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => e.groupId && setSelectedGroupId(e.groupId)}
                style={{ width: '100%', padding: '12px 16px', background: 'transparent', border: 'none', borderBottom: '1px solid #27272a', cursor: e.groupId ? 'pointer' : 'default', textAlign: 'left', color: '#e4e4e7' }}
              >
                <span style={{ fontSize: 14, fontWeight: 600 }}>{e.title}</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: '#71717a' }}>{formatTime(e.startTime)}</span>
              </button>
            ))}
          </div>
        )}

        <p style={{ margin: 0, padding: '12px 16px 4px', fontSize: 12, fontWeight: 600, color: '#71717a', textTransform: 'uppercase' }}>Your circles</p>
        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>Loading...</div>}
        {!loading && circleGroups.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
            <Users size={48} style={{ marginBottom: 12, opacity: 0.5 }} />
            <p>No circles yet</p>
          </div>
        )}
        {!loading && circleGroups.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setSelectedGroupId(g.id)}
            style={{ width: '100%', padding: 16, background: 'transparent', border: 'none', borderBottom: '1px solid #27272a', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#27272a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff9a56' }}>
              {g.type === 'dm' ? <MessageCircle size={20} /> : <Users size={20} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{groupLabel(g)}</span>
              {g.lastMessage && (
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#a1a1aa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.lastMessage}</p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Circle;
